import React from 'react';
import { Gauge, Clock, AlertCircle } from 'lucide-react';
import {
  formatHours,
  getNextServiceMilestone,
  type MilestoneProgress,
} from '../../services/scheduler';

export interface HoursTrackerProps {
  cumulativeHours: number;
  lastServiceHours?: number;
  compact?: boolean;
  onAddHoursClick?: () => void;
  className?: string;
}

export function HoursTracker({
  cumulativeHours,
  lastServiceHours = 0,
  compact = false,
  onAddHoursClick,
  className = '',
}: HoursTrackerProps) {
  const nextMilestone: MilestoneProgress = getNextServiceMilestone(
    cumulativeHours,
    lastServiceHours
  );

  const deltaHours = Math.max(0, cumulativeHours - lastServiceHours);
  const percent = Math.min(100, Math.max(0, nextMilestone.progressPercent));

  // Determine progress bar color theme
  let progressColor = 'bg-emerald-500';
  let progressBg = 'bg-emerald-50/50';
  let textColor = 'text-emerald-700';
  let badgeColor = 'bg-emerald-100 border-emerald-200 text-emerald-800';

  if (nextMilestone.status === 'OVERDUE') {
    progressColor = 'bg-rose-500 animate-pulse';
    progressBg = 'bg-rose-50';
    textColor = 'text-rose-700';
    badgeColor = 'bg-rose-100 border-rose-200 text-rose-800';
  } else if (nextMilestone.status === 'DUE_SOON') {
    progressColor = 'bg-amber-500';
    progressBg = 'bg-amber-50';
    textColor = 'text-amber-800';
    badgeColor = 'bg-amber-100 border-amber-200 text-amber-800';
  }

  if (compact) {
    return (
      <div className={`space-y-1.5 ${className}`}>
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-500 flex items-center gap-1">
            <Gauge className="w-3.5 h-3.5 text-blue-600" />
            ชั่วโมงสะสม:
          </span>
          <strong className="font-mono text-slate-800 font-bold">
            {formatHours(cumulativeHours)}
          </strong>
        </div>

        {/* Mini progress bar towards next milestone */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-slate-500 truncate max-w-[160px]">
              เป้า {nextMilestone.tier.toLocaleString()} ชม. ({nextMilestone.description.split('(')[0].trim()})
            </span>
            <span className={`font-mono font-bold ${textColor}`}>
              {percent.toFixed(1)}%
            </span>
          </div>

          <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${progressColor}`}
              style={{ width: `${percent}%` }}
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white border border-slate-200 rounded-xl p-4 space-y-3 shadow-xs ${className}`}>
      {/* Header: Cumulative Hours & Quick Action */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-blue-50 border border-blue-200 text-blue-600">
            <Gauge className="w-5 h-5" />
          </div>
          <div>
            <span className="text-xs text-slate-500 font-medium">ชั่วโมงทำงานสะสม (Operating Hours)</span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-black font-mono text-slate-900 tracking-tight">
                {formatHours(cumulativeHours, false)}
              </span>
              <span className="text-xs text-slate-500 font-bold">ชม. (Hours)</span>
            </div>
          </div>
        </div>

        {onAddHoursClick && (
          <button
            type="button"
            onClick={onAddHoursClick}
            className="text-xs bg-white hover:bg-slate-100 active:bg-slate-200 text-slate-700 px-2.5 py-1.5 rounded-lg border border-slate-300 font-medium transition-colors flex items-center gap-1 min-h-[36px] shadow-2xs"
          >
            + บันทึกชั่วโมง
          </button>
        )}
      </div>

      {/* Next Major Milestone Callout */}
      <div className={`rounded-lg border p-3 ${progressBg} border-slate-200 space-y-2`}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-xs font-semibold text-slate-700">
                เป้าหมายรอบบำรุงรักษาถัดไป: {nextMilestone.tier.toLocaleString()} ชม.
              </span>
            </div>
            <p className="text-xs font-bold text-slate-900 mt-0.5">
              {nextMilestone.description}
            </p>
          </div>

          <span
            className={`text-[11px] font-bold px-2 py-0.5 rounded-md border whitespace-nowrap ${badgeColor}`}
          >
            {nextMilestone.status === 'OVERDUE'
              ? 'เกินกำหนด'
              : nextMilestone.status === 'DUE_SOON'
              ? 'ใกล้ครบกำหนด'
              : 'ตามเกณฑ์'}
          </span>
        </div>

        {/* Visual Progress Bar */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs font-mono">
            <span className="text-slate-500">
              {deltaHours.toFixed(1)} / {nextMilestone.tier} ชม.
            </span>
            <span className={`font-bold ${textColor}`}>
              {percent.toFixed(1)}%
            </span>
          </div>

          <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${progressColor}`}
              style={{ width: `${percent}%` }}
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        </div>

        {/* Milestone Detail Footer */}
        <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1 border-t border-slate-200/80">
          <span>
            {nextMilestone.remainingHours > 0
              ? `เหลืออีก ${nextMilestone.remainingHours.toFixed(1)} ชม. (~${nextMilestone.daysRemaining} วัน)`
              : `เกินกำหนดมาแล้ว ${(deltaHours - nextMilestone.tier).toFixed(1)} ชม.`}
          </span>
          {nextMilestone.lubricants && nextMilestone.lubricants.length > 0 && (
            <span className="font-semibold text-blue-700">
              {nextMilestone.lubricants[0].code}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
